import {
  buildCurrentFormValues,
  isValueEmpty,
  shouldEndDueToFileDependency,
  shouldTriggerFormFiller
} from './FieldInteractionsHelper';
import type { FieldChangeHandler } from './fieldInteractionsTypes';
import type { UseFieldInteractionApiReturn } from './useFieldInteractionApi';
import { ContentTypeFieldResponse, SEARCHABLE_FIELD_DEFAULT_DEBOUNCE_TIMER } from '@cs-cms/types';
import type { useFieldState } from './useFieldState';

type FieldState = ReturnType<typeof useFieldState>;

interface CreateSearchableFieldHandlersParams {
  ctFields: ContentTypeFieldResponse[];
  fieldState: FieldState;
  callLoadFieldValuesApi: UseFieldInteractionApiReturn['callLoadFieldValuesApi'];
  applyLookupResponseToForm: UseFieldInteractionApiReturn['applyLookupResponseToForm'];
  handleDependencies: UseFieldInteractionApiReturn['handleDependencies'];
  runLookupForField: UseFieldInteractionApiReturn['runLookupForField'];
}

export interface SearchableFieldHandlers {
  onStringNumberFieldChange: FieldChangeHandler;
  onChoiceFilterChange: FieldChangeHandler;
}

export const createSearchableFieldHandlers = ({
  ctFields,
  fieldState,
  callLoadFieldValuesApi,
  applyLookupResponseToForm,
  handleDependencies,
  runLookupForField
}: CreateSearchableFieldHandlersParams): SearchableFieldHandlers => {
  const onStringNumberFieldChange: FieldChangeHandler = async (fieldName, value, formRenderProps) => {
    formRenderProps.onChange(fieldName, { value });

    const field = ctFields.find(f => f.name === fieldName);
    if (!field) {
      return;
    }

    if (shouldEndDueToFileDependency(field)) {
      return;
    }

    await handleDependencies(fieldName, value, formRenderProps);

    const currentFormValues = buildCurrentFormValues(fieldName, value, formRenderProps, ctFields);

    if (field.searchable) {
      if (shouldTriggerFormFiller(field, value)) {
        await runLookupForField(fieldName, value, formRenderProps);
        return;
      }

      fieldState.clearSearchTimer(fieldName);

      if (isValueEmpty(value)) {
        fieldState.clearFieldChoices(fieldName);
        return;
      }

      const timer = setTimeout(async () => {
        const response = await callLoadFieldValuesApi(fieldName, currentFormValues, String(value));

        if (response?.values && response.values.length > 0) {
          fieldState.setFieldChoices(fieldName, response.values);
        } else {
          fieldState.clearFieldChoices(fieldName);
        }

        fieldState.clearSearchTimer(fieldName);
      }, SEARCHABLE_FIELD_DEFAULT_DEBOUNCE_TIMER);

      fieldState.setSearchTimer(fieldName, timer);
      return;
    }

    if (shouldTriggerFormFiller(field, value)) {
      await runLookupForField(fieldName, value, formRenderProps);
    }
  };

  const onChoiceFilterChange: FieldChangeHandler = async (fieldName, value, formRenderProps) => {
    const field = ctFields.find(f => f.name === fieldName);
    if (!field?.searchable || !field.usesExtDictionary) {
      return;
    }

    fieldState.clearSearchTimer(fieldName);

    if (isValueEmpty(value)) {
      fieldState.clearFieldChoices(fieldName);
      return;
    }

    const currentFormValues = buildCurrentFormValues(fieldName, null, formRenderProps, ctFields);

    const timer = setTimeout(async () => {
      const response = await callLoadFieldValuesApi(fieldName, currentFormValues, String(value));

      if (response?.values && response.values.length > 0) {
        fieldState.setFieldChoices(fieldName, response.values);
      } else {
        fieldState.clearFieldChoices(fieldName);
      }

      fieldState.clearSearchTimer(fieldName);
    }, SEARCHABLE_FIELD_DEFAULT_DEBOUNCE_TIMER);

    fieldState.setSearchTimer(fieldName, timer);
  };

  return { onStringNumberFieldChange, onChoiceFilterChange };
};
